/**
 * CustomerProcessingScreen — Payment processing with QR code support
 * Shows merchant QR codes for Zelle/Venmo/Crypto payments
 */
import React, { useState, useEffect } from 'react';
import { getAccountRestaurant } from '@/lib/pwaAccount';

const API = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_MAP = {
  processing: 'Processing...',
  waiting_for_card: 'Please insert, tap, or swipe your card',
  card_inserted: 'Reading card...',
  processing_payment: 'Processing payment...',
};

const METHOD_LABELS = {
  zelle: 'Zelle',
  venmo: 'Venmo',
  crypto: 'Crypto',
};

export default function CustomerProcessingScreen({ total, paymentMethod, paymentStatus }) {
  const fmt = (n) => `$${Number(n).toFixed(2)}`;
  const statusText = STATUS_MAP[paymentStatus] || 'Waiting for payment...';
  const isQRMethod = ['zelle', 'venmo', 'crypto'].includes(paymentMethod?.toLowerCase());
  const [qrUrl, setQrUrl] = useState(null);
  const [qrLabel, setQrLabel] = useState('');

  useEffect(() => {
    // The QR codes the signed-in restaurant saved (POS / Back Office save
    // them under the restaurant's own id).
    const restaurantId = getAccountRestaurant()?.id;
    if (isQRMethod && restaurantId) {
      fetch(`${API}/api/display/payment-qr/${restaurantId}`)
        .then(r => r.json())
        .then(d => {
          if (d.config) {
            const key = `${paymentMethod.toLowerCase()}_qr_url`;
            const labelKey = `${paymentMethod.toLowerCase()}_label`;
            const url = d.config[key];
            if (url) setQrUrl(url.startsWith('/api/') ? `${API}${url}` : url);
            setQrLabel(d.config[labelKey] || '');
          }
        })
        .catch(() => {});
    }
  }, [paymentMethod, isQRMethod]);

  // QR-based payment screen
  if (isQRMethod && qrUrl) {
    return (
      <div className="cfd-panel cfd-center cfd-slide-up" data-testid="processing-screen">
        <h2 className="cfd-heading">Scan to Pay with {METHOD_LABELS[paymentMethod.toLowerCase()] || paymentMethod}</h2>
        <p className="cfd-total-display" style={{ marginBottom: '1.5rem' }}>{fmt(total)}</p>

        <div style={{
          background: '#fff',
          borderRadius: '16px',
          padding: '16px',
          display: 'inline-block',
          marginBottom: '1.5rem'
        }}>
          <img
            src={qrUrl}
            alt={`${paymentMethod} QR`}
            style={{ maxHeight: '280px', maxWidth: '280px', display: 'block' }}
            data-testid="customer-payment-qr-image"
          />
        </div>

        {qrLabel && <p className="cfd-subtext" style={{ fontSize: '1.1rem' }}>{qrLabel}</p>}
        <p className="cfd-subtext" style={{ opacity: 0.6 }}>Waiting for cashier to confirm payment...</p>

        <div className="cfd-bounce-dots" style={{ marginTop: '1rem' }}>
          <span /><span /><span />
        </div>
      </div>
    );
  }

  // Default processing screen
  return (
    <div className="cfd-panel cfd-center cfd-slide-up" data-testid="processing-screen">
      <p className="cfd-total-display">{fmt(total)}</p>

      <div className="cfd-processing-ring">
        <div className="cfd-ring-spinner" />
      </div>

      <h2 className="cfd-heading" style={{ marginTop: '2rem' }}>
        {paymentMethod === 'qr' ? 'Scan to Pay' : 'Processing Payment'}
      </h2>
      <p className="cfd-subtext">{statusText}</p>

      <div className="cfd-bounce-dots">
        <span /><span /><span />
      </div>
    </div>
  );
}
